import React, { useState, useEffect } from "react";
import {
  productAPI,
  Product,
  CreateProductData,
  UpdateProductData,
  ProductType,
  UNIT_OPTIONS,
  AvailabilityType,
  StatusType,
} from "../../services/productAPI";
import { categoryAPI } from "../../services/categoryAPI";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
  FormControlLabel,
  Checkbox,
  Divider,
  InputAdornment,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  RestaurantMenu as RecipeIcon,
  UploadFile as UploadFileIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useNavigate, Navigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import TableActionButton from "../../components/layout/TableActionButton";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import SortableTableCell from "../../components/layout/SortableTableCell";
import { useTableSort } from "../../hooks/useTableSort";
import { useRBAC } from "../../hooks/useRBAC";
import {
  WORK_CATEGORY_OPTIONS,
  uniqueNormalizedWorkCategories,
} from "../../constants/workCategories";
import { useTradeTerminology } from "../../utils/tradeTerminology";

type ProductSortField =
  | "name"
  | "sku"
  | "productType"
  | "category"
  | "sellingPrice"
  | "availability"
  | "status";

type ProductTypeFilter = "all" | ProductType;
type StatusFilter = "all" | StatusType;
type AvailabilityFilter = "all" | AvailabilityType;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const normalizeOptionalUuid = (value: unknown): string | null => {
  if (value == null || value === "") return null;
  return String(value);
};

const normalizeOptionalDiscountType = (
  value: unknown
): "PERCENT" | "FLAT" | null => {
  if (value == null || value === "") return null;
  return value as "PERCENT" | "FLAT";
};

const toOptionalNumber = (value: unknown): number | null | undefined => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toRequiredNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const ProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { terms } = useTradeTerminology();
  const { canCreate, canUpdate, canDelete, hasAnyRole } = useRBAC();
  const isAdminOrAbove = hasAnyRole("ADMIN", "SUPER_ADMIN", "PLATFORM_ADMIN");
  const isSuperAdmin = hasAnyRole("SUPER_ADMIN");
  const isPlatformAdmin = hasAnyRole("PLATFORM_ADMIN");
  const canImportExcel = isSuperAdmin || isPlatformAdmin || canCreate("products");
  const importFileRef = React.useRef<HTMLInputElement | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    total: number;
    created: number;
    skipped: number;
    failed: number;
    results: Array<{
      row: number;
      name: string;
      status: string;
      reason?: string;
    }>;
  } | null>(null);
  const { sortBy, sortOrder, handleSort, sortParams } =
    useTableSort<ProductSortField>("name", "asc");
  const onSort = (field: ProductSortField) => handleSort(field, () => setPage(0));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("search") || "";
  });
  const [debouncedSearch, setDebouncedSearch] = useState(() => {
    if (typeof window === "undefined") return "";
    return (new URLSearchParams(window.location.search).get("search") || "").trim();
  });
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [productTypeFilter, setProductTypeFilter] = useState<ProductTypeFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [availabilityFilter, setAvailabilityFilter] =
    useState<AvailabilityFilter>("all");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    product: Product | null;
  }>({
    open: false,
    product: null,
  });
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [apiValidationErrors, setApiValidationErrors] = useState<
    Record<string, string>
  >({});
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: categoriesData } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => categoryAPI.getCategories({ pageSize: 100 }),
  });

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const { data: subCategoriesData } = useQuery({
    queryKey: ["subcategories", selectedCategoryId],
    queryFn: async () => {
      if (!selectedCategoryId) return [];
      return categoryAPI.allsubcategories(selectedCategoryId);
    },
    enabled: !!selectedCategoryId,
  });

  const {
    data: productsData,
    isLoading,
    error: productsError,
  } = useQuery({
    queryKey: [
      "products",
      page,
      rowsPerPage,
      debouncedSearch,
      categoryFilter,
      productTypeFilter,
      statusFilter,
      availabilityFilter,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      productAPI.getProducts({
        pageSize: rowsPerPage,
        pageNo: page + 1,
        ...sortParams,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(categoryFilter !== "all" ? { categoryId: categoryFilter } : {}),
        ...(productTypeFilter !== "all" ? { productType: productTypeFilter } : {}),
        ...(statusFilter !== "all" ? { status: statusFilter } : {}),
        ...(availabilityFilter !== "all" ? { availability: availabilityFilter } : {}),
      }),
  });

  const { mutate: createProduct } = useMutation({
    mutationFn: (data: CreateProductData) => {
      console.log("Creating product with data:", data);
      return productAPI.createProduct(data);
    },
    onSuccess: () => {
      console.log("Product created successfully");
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setAlert({ type: "success", message: "Product created successfully!" });
      handleCloseDialog();
    },
    onError: (error: any) => {
      console.log("Product creation error:", error);
      const errorData = error.response?.data;
      if (errorData?.validation) {
        // Set individual field errors from API validation
        setApiValidationErrors(errorData.validation);
        setAlert({
          type: "error",
          message: errorData.message || "Validation failed",
        });
      } else {
        setAlert({
          type: "error",
          message: errorData?.message || "Failed to create product",
        });
      }
    },
  });

  const { mutate: updateProduct } = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductData }) => {
      console.log("Updating product with ID:", id, "and data:", data);
      return productAPI.updateProduct(id, data);
    },
    onSuccess: () => {
      console.log("Product updated successfully");
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setAlert({ type: "success", message: "Product updated successfully!" });
      handleCloseDialog();
    },
    onError: (error: any) => {
      console.log("Product update error:", error);
      const errorData = error.response?.data;
      if (errorData?.validation) {
        // Set individual field errors from API validation
        setApiValidationErrors(errorData.validation);
        setAlert({
          type: "error",
          message: errorData.message || "Validation failed",
        });
      } else {
        setAlert({
          type: "error",
          message: errorData?.message || "Failed to update product",
        });
      }
    },
  });

  const { mutate: toggleHideFromBilling, isPending: togglingHide } = useMutation({
    mutationFn: ({
      product,
      hideFromBilling,
    }: {
      product: Product;
      hideFromBilling: boolean;
    }) =>
      productAPI.updateProduct(product.id, {
        categoryId: product.categoryId,
        subCategoryId: product.subCategoryId,
        sku: product.sku,
        itemCode: product.itemCode || undefined,
        name: product.name,
        shortDescription: product.shortDescription || undefined,
        fullDescription: product.fullDescription || undefined,
        productType: product.productType,
        hsnCode: product.hsnCode || undefined,
        barcode: product.barcode || undefined,
        unit: product.unit,
        basePrice: Number(product.basePrice) || 0,
        purchasePrice: Number(product.purchasePrice) || 0,
        sellingPrice: Number(product.sellingPrice) || 0,
        discountType: product.discountType,
        discountValue: product.discountValue,
        gstPercentage: Number(product.gstPercentage) || 0,
        preparationTime: product.preparationTime,
        availability: product.availability,
        isTaxInclusive: Boolean(product.isTaxInclusive),
        status: product.status,
        hideFromBilling,
        displayOrder: Number(product.displayOrder) || 0,
        workCategories: product.workCategories,
        packSize: product.packSize,
        stockUnit: product.stockUnit,
        purchaseUnit: product.purchaseUnit,
      }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setAlert({
        type: "success",
        message: vars.hideFromBilling
          ? "Hidden from billing"
          : "Visible in billing again",
      });
    },
    onError: (error: any) => {
      setAlert({
        type: "error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update billing visibility",
      });
    },
  });

  const { mutate: deleteProduct } = useMutation({
    mutationFn: (id: string) => productAPI.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setAlert({ type: "success", message: "Product deleted successfully!" });
      setDeleteDialog({ open: false, product: null });
    },
    onError: (error: any) => {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Failed to delete product",
      });
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      categoryId: "",
      name: "",
      description: "",
      sku: "",
      unit: "PCS",
      unitPrice: 0,
      costPrice: 0,
      taxRate: 0,
      maximumStock: 0,
      productType: "GOODS",
      availability: "ALL",
      status: "ACTIVE",
      hideFromBilling: false,
      displayOrder: 0,
      workCategories: [],
      packSize: 1,
      stockUnit: "",
      purchaseUnit: "",
    },
  });

  // Debug: Watch for form errors
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log("Form validation errors:", errors);
    }
  }, [errors]);

  const watchedDiscountType = watch("discountType" as any);

  const watchedCategoryId = watch("categoryId");

  useEffect(() => {
    setSelectedCategoryId(watchedCategoryId);
  }, [watchedCategoryId]);

  const handleImportExcelClick = () => {
    importFileRef.current?.click();
  };

  const handleImportExcelFile = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const summary = await productAPI.importMasterExcel(file);
      setImportResult(summary);
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      setAlert({
        type: "success",
        message: `Import: ${summary.created} created, ${summary.skipped} skipped (duplicate name), ${summary.failed} failed`,
      });
    } catch (err: any) {
      setAlert({
        type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Excel import failed",
      });
    } finally {
      setImporting(false);
    }
  };

  const downloadImportTemplate = () => {
    // Same columns as Shah Mastana sheet (+ optional extras)
    const header =
      "Name,Category,Price,GST% (Allowed only 5,18,40),Type (F=forwardTax,B=BackwardTax),SKU,Unit,HSN,ProductType,PurchasePrice,WorkCategories\n";
    const sample =
      "Milk Badam,Cold Beverages,60,5,B,,PCS,21069099,PREPARED,,restaurant\n";
    const blob = new Blob([header + sample], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "master-products-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleOpenDialog = (product?: any) => {
    setEditingProduct(product);
    setApiValidationErrors({}); // Clear API validation errors

    // Debug: Log the product object to see what's available
    if (product) {
      console.log("Editing product data:", product);
      console.log("Product categoryId:", product.categoryId);
      console.log("Product category?.id:", product.category?.id);
      console.log("Product subCategoryId:", product.subCategoryId);
      console.log("Product subCategory?.id:", product.subCategory?.id);
      console.log("Product productType:", product.productType);
      console.log("Product availability:", product.availability);
      console.log("Product status:", product.status);
    }

    if (product) {
      const formData = {
        categoryId: product.categoryId || product.category?.id || "",
        subCategoryId: product.subCategoryId || product.subCategory?.id || null,
        productType: product.productType,
        name: product.name,
        sku: product.sku,
        itemCode: product.itemCode || "",
        hsnCode: product.hsnCode || "",
        barcode: product.barcode || "",
        unit: product.unit,
        shortDescription: product.shortDescription || "",
        fullDescription: product.fullDescription || "",
        basePrice: product.basePrice || 0,
        purchasePrice: product.purchasePrice || 0,
        sellingPrice: product.sellingPrice || 0,
        discountType: product.discountType || "",
        discountValue: product.discountValue || 0,
        gstPercentage: product.gstPercentage || 0,
        secondaryTaxRate: (product as any).secondaryTaxRate || 0,
        secondaryTaxType: (product as any).secondaryTaxType || "VAT",
        taxCalculationType: (product as any).taxCalculationType || "ADDITIVE",
        preparationTime: product.preparationTime || null,
        availability: product.availability || "ALL",
        isTaxInclusive: product.isTaxInclusive || false,
        status: product.status || "ACTIVE",
        hideFromBilling: Boolean(product.hideFromBilling),
        displayOrder: product.displayOrder || 0,
        workCategories: uniqueNormalizedWorkCategories(product.workCategories),
        packSize: product.packSize ?? 1,
        stockUnit: product.stockUnit || "",
        purchaseUnit: product.purchaseUnit || "",
      };

      console.log("Setting form data:", formData);
      reset(formData);

      // Set the selected category to trigger subcategory loading after a short delay
      setTimeout(() => {
        if (formData.categoryId) {
          console.log("Setting selected category ID:", formData.categoryId);
          setSelectedCategoryId(formData.categoryId);
        }
      }, 100);

      // Debug: Check form values after reset
      setTimeout(() => {
        console.log("Form values after reset:", {
          categoryId: watch("categoryId"),
          subCategoryId: watch("subCategoryId"),
          productType: watch("productType"),
          availability: watch("availability"),
          status: watch("status"),
        });
      }, 200);
    } else {
      reset({
        categoryId: "",
        subCategoryId: null,
        productType: "GOODS",
        name: "",
        sku: "",
        itemCode: "",
        hsnCode: "",
        barcode: "",
        unit: "PCS",
        shortDescription: "",
        fullDescription: "",
        basePrice: 0,
        purchasePrice: 0,
        sellingPrice: 0,
        discountType: "",
        discountValue: 0,
        gstPercentage: 0,
        secondaryTaxRate: 0,
        secondaryTaxType: "VAT",
        taxCalculationType: "ADDITIVE",
        preparationTime: null,
        availability: "ALL",
        isTaxInclusive: false,
        status: "ACTIVE",
        hideFromBilling: false,
        displayOrder: 0,
        workCategories: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProduct(null);
    setApiValidationErrors({}); // Clear API validation errors
    reset();
  };

  const onSubmit = (data: any) => {
    console.log("Form submitted with data:", data);
    console.log("Editing product:", editingProduct);

    try {
      if (editingProduct) {
        console.log("Updating product...");
        // For update, use the full data structure
        const discountType = normalizeOptionalDiscountType(data.discountType);
        const cleanedUpdateData: UpdateProductData = {
          categoryId: data.categoryId,
          subCategoryId: normalizeOptionalUuid(data.subCategoryId),
          name: data.name,
          shortDescription: data.shortDescription || undefined,
          fullDescription: data.fullDescription || undefined,
          productType: data.productType,
          sku: data.sku,
          itemCode: data.itemCode || undefined,
          hsnCode: data.hsnCode || undefined,
          barcode: data.barcode || undefined,
          unit: data.unit,
          basePrice: toRequiredNumber(data.basePrice),
          purchasePrice: toOptionalNumber(data.purchasePrice) ?? undefined,
          sellingPrice: toRequiredNumber(data.sellingPrice),
          discountType,
          discountValue: discountType
            ? toOptionalNumber(data.discountValue) ?? undefined
            : null,
          gstPercentage: toOptionalNumber(data.gstPercentage) ?? undefined,
          secondaryTaxRate: toOptionalNumber(data.secondaryTaxRate) ?? 0,
          secondaryTaxType: data.secondaryTaxType || null,
          taxCalculationType: data.taxCalculationType || "ADDITIVE",
          preparationTime:
            data.preparationTime == null || data.preparationTime === ""
              ? null
              : Number(data.preparationTime),
          availability: data.availability,
          isTaxInclusive: data.isTaxInclusive,
          status: data.status || "ACTIVE",
          hideFromBilling: Boolean(data.hideFromBilling),
          displayOrder: Number(data.displayOrder) || 0,
          workCategories: uniqueNormalizedWorkCategories(data.workCategories),
          packSize: toOptionalNumber(data.packSize) ?? 1,
          stockUnit: data.stockUnit || null,
          purchaseUnit: data.purchaseUnit || null,
        };
        updateProduct({ id: editingProduct.id, data: cleanedUpdateData });
      } else {
        console.log("Creating product...");
        // For create, map form fields to API structure
        const createDiscountType = normalizeOptionalDiscountType(data.discountType);
        const cleanedCreateData: CreateProductData = {
          categoryId: data.categoryId,
          subCategoryId: normalizeOptionalUuid(data.subCategoryId) ?? undefined,
          name: data.name,
          description: data.description || undefined,
          shortDescription: data.shortDescription || undefined,
          fullDescription: data.fullDescription || undefined,
          sku: data.sku,
          unit: data.unit,
          purchasePrice: toRequiredNumber(data.purchasePrice),
          sellingPrice: toRequiredNumber(data.sellingPrice),
          basePrice: toRequiredNumber(data.basePrice),
          gstPercentage: toRequiredNumber(data.gstPercentage),
          secondaryTaxRate: toOptionalNumber(data.secondaryTaxRate) ?? 0,
          secondaryTaxType: data.secondaryTaxType || null,
          taxCalculationType: data.taxCalculationType || "ADDITIVE",
          productType: data.productType || "GOODS",
          availability: data.availability || "ALL",
          displayOrder: Number(data.displayOrder) || 0,
          barcode: data.barcode || undefined,
          itemCode: data.itemCode || undefined,
          hsnCode: data.hsnCode || undefined,
          preparationTime: data.preparationTime || undefined,
          discountType: createDiscountType ?? undefined,
          discountValue: createDiscountType
            ? toOptionalNumber(data.discountValue) ?? undefined
            : undefined,
          isTaxInclusive: data.isTaxInclusive || false,
          hideFromBilling: Boolean(data.hideFromBilling),
          workCategories: uniqueNormalizedWorkCategories(data.workCategories),
          packSize: toOptionalNumber(data.packSize) ?? 1,
          stockUnit: data.stockUnit || null,
          purchaseUnit: data.purchaseUnit || null,
        };
        console.log("Create data:", cleanedCreateData);
        createProduct(cleanedCreateData);
      }
    } catch (error) {
      console.error("Error in onSubmit:", error);
      setAlert({
        type: "error",
        message: "An error occurred while processing the form",
      });
    }
  };

  // Test function to manually trigger form submission
  // const testFormSubmission = () => {
  //   console.log("Testing form submission...");
  //   console.log("handleSubmit function:", handleSubmit);
  //   console.log("onSubmit function:", onSubmit);

  //   try {
  //     const submitFunction = handleSubmit(onSubmit);
  //     console.log("Submit function created:", submitFunction);
  //     submitFunction();
  //     console.log("Submit function executed");
  //   } catch (error) {
  //     console.error("Error executing submit function:", error);
  //   }
  // };

  const handleDelete = (product: Product) => {
    setDeleteDialog({ open: true, product });
  };

  const products = productsData?.data || [];
  const totalCount = productsData?.total || 0;
  const categories = categoriesData?.data || [];

  const getStatusColor = (status: StatusType) => {
    return status === "ACTIVE" ? "success" : "error";
  };

  const getProductTypeColor = (productType: ProductType) => {
    return productType === "GOODS" ? "primary" : "secondary";
  };

  const getAvailabilityChip = (availability: AvailabilityType) => {
    const labels = {
      DINE_IN: "Dine In",
      TAKEAWAY: "Takeaway",
      DELIVERY: "Delivery",
      ALL: "All Channels",
    } as const;
    return labels[availability];
  };

  const filterDropdowns = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      flexWrap="wrap"
      sx={{ width: "100%" }}
    >
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
        <InputLabel>Category</InputLabel>
        <Select
          value={categoryFilter}
          label="Category"
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Categories</MenuItem>
          {categories.map((cat) => (
            <MenuItem key={cat.id} value={cat.id}>
              {cat.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
        <InputLabel>Type</InputLabel>
        <Select
          value={productTypeFilter}
          label="Type"
          onChange={(e) => {
            setProductTypeFilter(e.target.value as ProductTypeFilter);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Types</MenuItem>
          <MenuItem value="GOODS">Goods</MenuItem>
          <MenuItem value="PREPARED">Prepared</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={statusFilter}
          label="Status"
          onChange={(e) => {
            setStatusFilter(e.target.value as StatusFilter);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="INACTIVE">Inactive</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
        <InputLabel>Availability</InputLabel>
        <Select
          value={availabilityFilter}
          label="Availability"
          onChange={(e) => {
            setAvailabilityFilter(e.target.value as AvailabilityFilter);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Channels</MenuItem>
          <MenuItem value="DINE_IN">Dine In</MenuItem>
          <MenuItem value="TAKEAWAY">Takeaway</MenuItem>
          <MenuItem value="DELIVERY">Delivery</MenuItem>
          <MenuItem value="ALL">All</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );

  if (!isAdminOrAbove) {
    return <Navigate to="/product-list" replace />;
  }

  return (
    <Box>
      <PageHeader
        title={terms.productMasterLabel}
        subtitle="Shared catalog — any company can import products into their sales points"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: terms.productMasterLabel },
        ]}
        action={
          <>
            <Tooltip title="Export PDF">
              <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export Excel">
              <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                <ExcelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canImportExcel && (
              <>
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  hidden
                  onChange={handleImportExcelFile}
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={handleImportExcelClick}
                  disabled={importing}
                >
                  {importing ? "Importing…" : "Import Excel"}
                </Button>
                <Button size="small" onClick={downloadImportTemplate}>
                  Template
                </Button>
              </>
            )}
            {canCreate("products") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                {terms.productMasterAddButton}
              </Button>
            )}
          </>
        }
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {productsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {productsError instanceof Error
            ? productsError.message
            : "Failed to load products"}
        </Alert>
      )}

      <DataListCard
        searchPlaceholder="Search name, SKU, barcode…"
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        filters={filterDropdowns}
        footer={
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ width: "100%" }}
          />
        }
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <Typography color="text.secondary">Loading products…</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell width={52}>S.No.</TableCell>
                  <SortableTableCell label="Product" field="name" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="SKU" field="sku" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Type" field="productType" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Category" field="category" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Price" field="sellingPrice" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Availability" field="availability" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Status" field="status" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <TableCell
                    align="center"
                    width={56}
                    title="Hide from billing"
                    sx={{ width: 56, maxWidth: 56, px: 0.5 }}
                  >
                    Hide
                  </TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product: Product, index: number) => (
                  <TableRow key={product.id} hover>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {page * rowsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box minWidth={0}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {product.name}
                        </Typography>
                        {product.shortDescription && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {product.shortDescription}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{product.sku}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.productType}
                        color={getProductTypeColor(product.productType)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {product.category?.name || "—"}
                      </Typography>
                      {product.subCategory && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {product.subCategory.name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(product.sellingPrice)}
                      </Typography>
                      {product.discountValue ? (
                        <Typography variant="caption" color="success.main">
                          {product.discountType === "PERCENT"
                            ? `${product.discountValue}% off`
                            : `${formatCurrency(product.discountValue)} off`}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getAvailabilityChip(product.availability)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.status}
                        color={getStatusColor(product.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ width: 56, maxWidth: 56, px: 0.5 }}
                    >
                      <Checkbox
                        size="small"
                        checked={Boolean(product.hideFromBilling)}
                        disabled={!canUpdate("products") || togglingHide}
                        onChange={(_, checked) =>
                          toggleHideFromBilling({
                            product,
                            hideFromBilling: checked,
                          })
                        }
                        inputProps={{ "aria-label": "Hide from billing" }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {product.productType === "PREPARED" && (
                          <TableActionButton
                            title="Recipes / BOM"
                            variant="default"
                            onClick={() =>
                              navigate(`/recipes?productId=${product.id}`)
                            }
                          >
                            <RecipeIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                        {canUpdate("products") && (
                          <TableActionButton
                            title="Edit product"
                            variant="primary"
                            onClick={() => handleOpenDialog(product)}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                        {canDelete("products") && (
                          <TableActionButton
                            title="Delete product"
                            variant="danger"
                            onClick={() => handleDelete(product)}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {products.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No products found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataListCard>


      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700}>
                {editingProduct ? `Edit ${terms.itemSingular}` : `Add New ${terms.itemSingular}`}
              </Typography>
              <IconButton size="small" onClick={handleCloseDialog}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </DialogTitle>
          {Object.keys(apiValidationErrors).length > 0 && (
            <Box sx={{ px: 3, pb: 2 }}>
              <Alert severity="error" sx={{ mb: 0 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Validation Errors:
                </Typography>
                {Object.entries(apiValidationErrors).map(([field, message]) => (
                  <Typography key={field} variant="body2" sx={{ ml: 1 }}>
                    • {field}: {message}
                  </Typography>
                ))}
              </Alert>
            </Box>
          )}
          <DialogContent dividers sx={{ maxHeight: "75vh" }}>
            {alert && (
              <Alert
                severity={alert.type}
                sx={{ mb: 2 }}
                onClose={() => setAlert(null)}
              >
                {alert.message}
              </Alert>
            )}
            <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="productType"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          error={!!(errors as any).productType}
                        >
                          <InputLabel>Product Type *</InputLabel>
                          <Select {...field} label="Product Type *">
                            <MenuItem value="GOODS">Goods (Purchased)</MenuItem>
                            <MenuItem value="PREPARED">
                              Prepared (In-house)
                            </MenuItem>
                          </Select>
                          {(errors as any).productType && (
                            <FormHelperText>
                              {(errors as any).productType.message?.toString()}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="categoryId"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.categoryId}>
                          <InputLabel>Category *</InputLabel>
                          <Select
                            value={field.value ?? ""}
                            label="Category *"
                            onChange={(e) => {
                              const v = e.target.value;
                              field.onChange(v);
                              setValue("subCategoryId", null);
                            }}
                          >
                            <MenuItem value="">Select Category</MenuItem>
                            {categoriesData?.data?.map((category: any) => (
                              <MenuItem key={category.id} value={category.id}>
                                {category.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.categoryId && (
                            <FormHelperText>
                              {errors.categoryId.message?.toString()}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="subCategoryId"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          error={!!(errors as any).subCategoryId}
                        >
                          <InputLabel>Sub Category (optional)</InputLabel>
                          <Select
                            value={field.value ?? ""}
                            label="Sub Category (optional)"
                            onChange={(e) => {
                              const v = e.target.value;
                              field.onChange(v === "" ? null : v);
                            }}
                          >
                            <MenuItem value="">No Sub Category</MenuItem>
                            {subCategoriesData?.map((subCategory: any) => (
                              <MenuItem
                                key={subCategory.id}
                                value={subCategory.id}
                              >
                                {subCategory.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {(errors as any).subCategoryId && (
                            <FormHelperText>
                              {(
                                errors as any
                              ).subCategoryId.message?.toString()}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="workCategories"
                      control={control}
                      defaultValue={[]}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Work categories</InputLabel>
                          <Select
                            multiple
                            value={field.value || []}
                            onChange={(e) => {
                              const v = e.target.value;
                              field.onChange(
                                typeof v === "string" ? v.split(",") : v
                              );
                            }}
                            label="Work categories"
                            renderValue={(selected) => (
                              <Box
                                sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                              >
                                {(selected as string[]).map((value) => {
                                  const opt = WORK_CATEGORY_OPTIONS.find(
                                    (o) => o.value === value
                                  );
                                  return (
                                    <Chip
                                      key={value}
                                      label={opt?.label || value}
                                      size="small"
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            {WORK_CATEGORY_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Tag products for sales-point import filtering
                          </FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Product Name *"
                          error={!!errors.name}
                          helperText={errors.name?.message?.toString()}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="sku"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="SKU *"
                          error={!!errors.sku}
                          helperText={errors.sku?.message?.toString()}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="barcode"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Barcode"
                          error={!!(errors as any).barcode}
                          helperText={(
                            errors as any
                          ).barcode?.message?.toString()}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="itemCode"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Item Code"
                          error={!!(errors as any).itemCode}
                          helperText={(
                            errors as any
                          ).itemCode?.message?.toString()}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="hsnCode"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="HSN Code"
                          error={!!(errors as any).hsnCode}
                          helperText={(
                            errors as any
                          ).hsnCode?.message?.toString()}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="unit"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.unit}>
                          <InputLabel>Unit *</InputLabel>
                          <Select {...field} label="Unit *">
                            {UNIT_OPTIONS.map((u) => (
                              <MenuItem key={u.value} value={u.value}>
                                {u.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.unit && (
                            <FormHelperText>
                              {errors.unit.message?.toString()}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="packSize"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Pack size"
                          helperText="Units per BOX/PACK (e.g. 12 PCS)"
                          inputProps={{ min: 0.0001, step: "0.01" }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="stockUnit"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Stock unit</InputLabel>
                          <Select {...field} label="Stock unit">
                            <MenuItem value="">Same as unit</MenuItem>
                            {UNIT_OPTIONS.map((u) => (
                              <MenuItem key={u.value} value={u.value}>
                                {u.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="purchaseUnit"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Purchase unit</InputLabel>
                          <Select {...field} label="Purchase unit">
                            <MenuItem value="">Same as unit</MenuItem>
                            {UNIT_OPTIONS.map((u) => (
                              <MenuItem key={u.value} value={u.value}>
                                {u.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="preparationTime"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Preparation Time (minutes)"
                          error={
                            !!(errors as any).preparationTime ||
                            !!apiValidationErrors.preparationTime
                          }
                          helperText={
                            (
                              errors as any
                            ).preparationTime?.message?.toString() ||
                            apiValidationErrors.preparationTime ||
                            ""
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="shortDescription"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Short Description"
                          multiline
                          rows={2}
                          error={!!(errors as any).shortDescription}
                          helperText={(
                            errors as any
                          ).shortDescription?.message?.toString()}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="fullDescription"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Full Description"
                          multiline
                          rows={4}
                          error={!!(errors as any).fullDescription}
                          helperText={(
                            errors as any
                          ).fullDescription?.message?.toString()}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Pricing & tax
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="basePrice"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Base Price *"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                ₹
                              </InputAdornment>
                            ),
                          }}
                          error={!!(errors as any).basePrice}
                          helperText={(
                            errors as any
                          ).basePrice?.message?.toString()}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="purchasePrice"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Purchase Price"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                ₹
                              </InputAdornment>
                            ),
                          }}
                          error={!!(errors as any).purchasePrice}
                          helperText={(
                            errors as any
                          ).purchasePrice?.message?.toString()}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name="sellingPrice"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Selling Price *"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                ₹
                              </InputAdornment>
                            ),
                          }}
                          error={!!(errors as any).sellingPrice}
                          helperText={(
                            errors as any
                          ).sellingPrice?.message?.toString()}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="discountType"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          error={
                            !!(errors as any).discountType ||
                            !!apiValidationErrors.discountType
                          }
                        >
                          <InputLabel>Discount Type (optional)</InputLabel>
                          <Select
                            value={field.value ?? ""}
                            label="Discount Type (optional)"
                            onChange={(e) => {
                              const v = e.target.value;
                              field.onChange(v === "" ? null : v);
                            }}
                          >
                            <MenuItem value="">No Discount</MenuItem>
                            <MenuItem value="PERCENT">Percentage</MenuItem>
                            <MenuItem value="FLAT">Flat Amount</MenuItem>
                          </Select>
                          {(errors as any).discountType && (
                            <FormHelperText>
                              {(errors as any).discountType.message?.toString()}
                            </FormHelperText>
                          )}
                          {apiValidationErrors.discountType && (
                            <FormHelperText error>
                              {apiValidationErrors.discountType}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {watchedDiscountType && (
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="discountValue"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            type="number"
                            label={
                              watchedDiscountType === "PERCENT"
                                ? "Discount %"
                                : "Discount Amount"
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  {watchedDiscountType === "PERCENT"
                                    ? "%"
                                    : "₹"}
                                </InputAdornment>
                              ),
                            }}
                            error={
                              !!(errors as any).discountValue ||
                              !!apiValidationErrors.discountValue
                            }
                            helperText={
                              (
                                errors as any
                              ).discountValue?.message?.toString() ||
                              apiValidationErrors.discountValue ||
                              ""
                            }
                          />
                        )}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="gstPercentage"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Primary Tax / GST %"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                          }}
                          error={!!(errors as any).gstPercentage}
                          helperText={(
                            errors as any
                          ).gstPercentage?.message?.toString()}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="secondaryTaxRate"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Secondary Tax / VAT %"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="secondaryTaxType"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel id="secondary-tax-type-label">Secondary Tax Type</InputLabel>
                          <Select
                            {...field}
                            labelId="secondary-tax-type-label"
                            label="Secondary Tax Type"
                            value={field.value || "VAT"}
                          >
                            <MenuItem value="VAT">VAT</MenuItem>
                            <MenuItem value="CESS">Cess</MenuItem>
                            <MenuItem value="EXCISE">Excise</MenuItem>
                            <MenuItem value="LOCAL_TAX">Local / State Surcharge</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="taxCalculationType"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel id="tax-calc-type-label">Tax Calculation Mode</InputLabel>
                          <Select
                            {...field}
                            labelId="tax-calc-type-label"
                            label="Tax Calculation Mode"
                            value={field.value || "ADDITIVE"}
                          >
                            <MenuItem value="ADDITIVE">Additive (Separate on base)</MenuItem>
                            <MenuItem value="COMPOUND">Compound (Tax on base + primary tax)</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="isTaxInclusive"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={field.value} />}
                          label="Tax Inclusive Price"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Availability & settings
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="maximumStock"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Maximum Stock"
                          error={!!(errors as any).maximumStock}
                          helperText={(
                            errors as any
                          ).maximumStock?.message?.toString()}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="availability"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          error={!!(errors as any).availability}
                        >
                          <InputLabel>Availability *</InputLabel>
                          <Select {...field} label="Availability *">
                            <MenuItem value="DINE_IN">Dine In Only</MenuItem>
                            <MenuItem value="TAKEAWAY">Takeaway Only</MenuItem>
                            <MenuItem value="DELIVERY">Delivery Only</MenuItem>
                            <MenuItem value="ALL">All Channels</MenuItem>
                          </Select>
                          {(errors as any).availability && (
                            <FormHelperText>
                              {(errors as any).availability.message?.toString()}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="displayOrder"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Display Order"
                          error={!!(errors as any).displayOrder}
                          helperText={(
                            errors as any
                          ).displayOrder?.message?.toString()}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!(errors as any).status}>
                          <InputLabel>Status *</InputLabel>
                          <Select {...field} label="Status *">
                            <MenuItem value="ACTIVE">Active</MenuItem>
                            <MenuItem value="INACTIVE">Inactive</MenuItem>
                          </Select>
                          {(errors as any).status && (
                            <FormHelperText>
                              {(errors as any).status.message?.toString()}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="hideFromBilling"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(field.value)}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          }
                          label="Hide from billing (POS grid / search)"
                        />
                      )}
                    />
                  </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            {/* <Button
              onClick={() => {
                console.log("Test button clicked");
                console.log("Current form values:", {
                  name: watch("name"),
                  sku: watch("sku"),
                  categoryId: watch("categoryId"),
                  productType: watch("productType"),
                });
                console.log("Current errors:", errors);
                testFormSubmission();
              }}
              color="secondary"
            >
              Test Submit
            </Button> */}
            <Button type="submit" variant="contained">
              {editingProduct ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, product: null })}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete product</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteDialog.product?.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, product: null })}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              deleteDialog.product && deleteProduct(deleteDialog.product.id)
            }
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!importResult}
        onClose={() => setImportResult(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Import result</DialogTitle>
        <DialogContent>
          {importResult && (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                Total {importResult.total} · Created {importResult.created} ·
                Skipped {importResult.skipped} · Failed {importResult.failed}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Duplicate item names are skipped. Missing categories are created
                automatically.
              </Typography>
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importResult.results.slice(0, 200).map((r, i) => (
                      <TableRow key={`${r.row}-${i}`}>
                        <TableCell>{r.row}</TableCell>
                        <TableCell>{r.name || "—"}</TableCell>
                        <TableCell>{r.status}</TableCell>
                        <TableCell>{r.reason || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResult(null)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductsPage;
